import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Snackbar, Stack, CircularProgress, FormControl, InputLabel,
  Select, MenuItem, OutlinedInput, Checkbox, ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { tenantApi } from '../../api/tenantApi';
import { useReadOnly } from '../../context/ReadOnlyContext';
import ApiExplorer from './ApiExplorer';

const ALL_SCOPES = [
  'inventory:read', 'inventory:write',
  'warehouses:read', 'warehouses:write',
  'reports:read',
];

export default function ApiKeysPage() {
  const { isReadOnly } = useReadOnly();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', scopes: ['inventory:read', 'inventory:write'], expiresInDays: '' });
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState(null); // the raw key shown once

  // Confirm revoke
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tenantApi.listApiKeys();
      setKeys(data.keys || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await tenantApi.createApiKey({
        name: form.name,
        scopes: form.scopes,
        expiresInDays: form.expiresInDays ? parseInt(form.expiresInDays, 10) : undefined,
      });
      setNewKey(res.key);
      setCreateOpen(false);
      setForm({ name: '', scopes: ['inventory:read', 'inventory:write'], expiresInDays: '' });
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
    finally { setSaving(false); }
  };

  const handleRevoke = async () => {
    try {
      await tenantApi.revokeApiKey(revokeTarget.keyId);
      setToast({ open: true, message: `Key "${revokeTarget.name}" revoked.`, severity: 'info' });
      setRevokeTarget(null);
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  const handleRotate = async (keyId, name) => {
    try {
      const res = await tenantApi.rotateApiKey(keyId);
      setNewKey(res.key);
      setToast({ open: true, message: `Key "${name}" rotated. New key shown below.`, severity: 'success' });
      load();
    } catch (e) { setToast({ open: true, message: e.message, severity: 'error' }); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToast({ open: true, message: 'Copied to clipboard.', severity: 'success' });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>API Keys</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage API keys for third-party integrations. Keys are shown only once at creation.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} disabled={isReadOnly}>
          Create Key
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key Prefix</TableCell>
              <TableCell>Scopes</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No API keys yet. Create one to get started.
                </TableCell>
              </TableRow>
            ) : keys.map((k) => (
              <TableRow key={k.keyId} hover>
                <TableCell fontWeight={500}>{k.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{k.keyPrefix}…</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {(k.scopes || []).map((s) => (
                      <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: 10 }} />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : 'Never'}
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Rotate key">
                    <IconButton size="small" onClick={() => handleRotate(k.keyId, k.name)} disabled={isReadOnly}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Revoke key">
                    <IconButton size="small" color="error" onClick={() => setRevokeTarget(k)} disabled={isReadOnly}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <ApiExplorer />

      {/* Create key dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Key Name *" size="small" fullWidth value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              helperText="A descriptive name, e.g. 'Shopify Integration'" />

            <FormControl size="small" fullWidth>
              <InputLabel>Scopes</InputLabel>
              <Select
                multiple
                value={form.scopes}
                onChange={(e) => setForm((f) => ({ ...f, scopes: e.target.value }))}
                input={<OutlinedInput label="Scopes" />}
                renderValue={(selected) => selected.join(', ')}
              >
                {ALL_SCOPES.map((s) => (
                  <MenuItem key={s} value={s}>
                    <Checkbox checked={form.scopes.includes(s)} size="small" />
                    <ListItemText primary={s} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Expires in (days)" size="small" fullWidth type="number"
              value={form.expiresInDays}
              onChange={(e) => setForm((f) => ({ ...f, expiresInDays: e.target.value }))}
              helperText="Leave blank for no expiry"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.name || !form.scopes.length}>
            {saving ? 'Creating…' : 'Create Key'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New key reveal dialog */}
      <Dialog open={!!newKey} onClose={() => setNewKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Your New API Key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now — it will not be shown again.
          </Alert>
          <Stack direction="row" spacing={1} alignItems="center"
            sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
            <Box flex={1}>{newKey}</Box>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => copyToClipboard(newKey)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewKey(null)}>I&apos;ve saved it</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm revoke */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs">
        <DialogTitle>Revoke API Key?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Revoking <strong>&quot;{revokeTarget?.name}&quot;</strong> will immediately invalidate it.
            Any integrations using this key will stop working.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRevoke}>Revoke</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
